import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { JobDefinition, JobResultDestinations } from '../JobDefinition';

/**
 * The generation tasks `run_inference.py` supports, with the input format
 * each one expects. Attachment points are written `[*]`. `de_novo` is the
 * only task that takes no starting structure at all, which is what every
 * `generation_task !== 'de_novo'` check below keys off.
 */
const MOLEXAR_TASKS = [
    { value: 'de_novo', label: 'De novo (no input SMILES)' },
    {
        value: 'scaffold_decoration',
        label: 'Scaffold decoration',
        hint: 'One [*] marks where new R-groups are added.',
        example: 'CC1=CN(C2=CC(CN(C3=NC=C(CC4=CC=C(N5C=CC=N5)C=C4[*])S3)CC6)=C6C=C2)C(C)=N1',
    },
    {
        value: 'motif_extension',
        label: 'Motif extension',
        hint: 'One [*] marks where the motif is grown.',
        example: '[*]N1C=CC=N1',
    },
    {
        value: 'linker_design',
        label: 'Linker design',
        hint: 'Two fragments (period-separated); each [*] is a point to be linked.',
        example: 'CC1=CN([*])C(C)=N1.[*]N1C=CC=N1',
    },
    {
        value: 'scaffold_morphing',
        label: 'Scaffold morphing',
        hint: 'Two [*]-tagged fragments; the scaffold connecting them is redesigned.',
        example: 'CC1=CN(C2=CC(CN([*])CC3)=C3C=C2)C(C)=N1.[*]CC1=CC=C(N2C=CC=N2)C=C1',
    },
    {
        value: 'superstructure',
        label: 'Superstructure',
        hint: 'No [*]; every atom of the input is retained in the output.',
        example: 'CC1=CN(C2=CC(CN(C3=NC=C(CC4=CC=C(N5C=CC=N5)C=C4)S3)CC6)=C6C=C2)C(C)=N1',
    },
];

function taskFor(formState) {
    return MOLEXAR_TASKS.find((t) => t.value === formState.generation_task);
}

/**
 * Optional property-conditioning knobs — each becomes a `run_inference.py`
 * float flag, and each is off when left blank (`deriveFormValues` drops
 * empty fields before the request is built). `mol_wt`/`mol_logp` are the
 * two in routine use, so they sit in their own section; the rest stay
 * under Advanced to keep the panel readable.
 */
const MOLEXAR_PROPERTIES = [
    {
        key: 'mol_wt', label: 'Molecular weight', primary: true, tooltip: 'Target molecular weight in Daltons. Blank = unconditioned.',
    },
    {
        key: 'mol_logp', label: 'LogP', primary: true, tooltip: 'Target calculated logP. Blank = unconditioned.',
    },
    { key: 'mol_hac', label: 'Heavy atom count', tooltip: 'Target number of heavy atoms. Blank = unconditioned.' },
    { key: 'mol_hbdc', label: 'H-bond donors', tooltip: 'Target hydrogen-bond donor count. Blank = unconditioned.' },
    { key: 'mol_hbac', label: 'H-bond acceptors', tooltip: 'Target hydrogen-bond acceptor count. Blank = unconditioned.' },
    { key: 'mol_rotbc', label: 'Rotatable bonds', tooltip: 'Target rotatable-bond count. Blank = unconditioned.' },
    { key: 'mol_tpsa', label: 'TPSA', tooltip: 'Target topological polar surface area. Blank = unconditioned.' },
    { key: 'mol_qed', label: 'QED', tooltip: 'Target QED drug-likeness score (0-1). Blank = unconditioned.' },
    { key: 'mol_sas', label: 'SA score', tooltip: 'Target synthetic accessibility score. Blank = unconditioned.' },
];

/** The property knobs for one section, as a `fields` schema. */
function propertyFields(primary) {
    return Object.fromEntries(
        MOLEXAR_PROPERTIES
            .filter((prop) => !!prop.primary === primary)
            .map(({ key, label, tooltip }) => [key, { widget: 'number', label, tooltip }])
    );
}

/**
 * Molexar pocket-conditioned generation job.
 *
 * Unlike DrugFlow (SDF/3D output), Molexar emits a plain `.smi` file -- one
 * canonical SMILES per line, no header, no 3D coordinates -- so this mirrors a
 * SMILES-producing job (Reinvent) for import: each line becomes a compound
 * carrying its SMILES, and 3D structure is generated on import.
 *
 * @augments JobDefinition
 */
export class MolexarJob extends JobDefinition {
    static jobTypeId = 'molexar';
    static resultDestination = JobResultDestinations.COMPOUND;
    static jobTypeName = 'Molexar';
    static jobName = 'Molexar Job';

    /** Results rows keyed by their generated id. */
    static idField = 'id';

    /**
     * Declarative description of this job's form — data only, no rendering
     * (see `formHelpers.js`). The embedding tab owns the program picker,
     * job-name field and submit button, so none of those appear here.
     *
     * `refLigandSpec` uses the same `pocket` widget DrugFlow does: both
     * tools take "a protein plus the ligand that defines the pocket", and
     * the widget's resolver turns one resSpec into `{ proteinPdb,
     * ligandSdf }` for free. Molexar's own pocket cropping happens
     * container-side in `molexar_run.py`, so the request shape is the same
     * as DrugFlow's despite the different cropping rule.
     *
     * `startStructure` is `visible`/`required` as a function of
     * `generation_task` — every task except `de_novo` needs a starting
     * structure, and `de_novo` must not be sent one. `visible` is honoured
     * both when rendering (`FieldList.jsx`) and when deriving the payload
     * (`formHelpers.js`), so switching back to `de_novo` drops the field's
     * value from the request without any extra bookkeeping here.
     * @type {object}
     */
    static jobForm = {
        body: {
            title: 'Specify Compound Input',
            fields: {
                refLigandSpec: {
                    widget: 'pocket',
                    label: 'Reference Ligand (defines pocket)',
                    default: '',
                    required: true,
                    requiredMessage: 'Select a reference ligand to define the binding pocket',
                },
                num_samples: {
                    widget: 'number', label: 'Number of molecules', default: 10, min: 1,
                },
                generation_task: {
                    widget: 'select',
                    label: 'Generation task',
                    default: 'de_novo',
                    options: MOLEXAR_TASKS.map(({ value, label }) => ({ value, label })),
                },
                startStructure: {
                    widget: 'moleculeSketcher',
                    label: 'Input structure (SMILES)',
                    placeholder: 'e.g. [*]N1C=CC=N1',
                    visible: (formState) => formState.generation_task !== 'de_novo',
                    required: (formState) => {
                        const { smiles, molfile } = formState.startStructure || {};
                        return !molfile && !(smiles || '').trim();
                    },
                    requiredMessage: 'This generation task requires an input structure (draw it or type SMILES)',
                    hintFrom: (formState) => taskFor(formState)?.hint,
                    exampleFrom: (formState) => taskFor(formState)?.example,
                },
            },
            subsections: [
                {
                    title: 'Property Conditioning',
                    collapsible: true,
                    fields: propertyFields(true),
                },
                {
                    title: 'Advanced Parameters',
                    collapsible: true,
                    fields: {
                        pocket_radius: {
                            widget: 'number',
                            label: 'Pocket radius (A)',
                            tooltip: 'Pocket crop radius: ATOM records within this distance of any reference-ligand heavy atom are kept. Leave blank for the container default (10 A).',
                            min: 1,
                        },
                        max_atoms: {
                            widget: 'number',
                            label: 'Max pocket atoms',
                            tooltip: 'Upper bound on the number of pocket atoms handed to the model.',
                            min: 1,
                        },
                        temperature: {
                            widget: 'number',
                            label: 'Temperature',
                            tooltip: 'Sampling temperature. Higher values give more diverse, less conservative molecules.',
                            min: 0,
                        },
                        top_p: {
                            widget: 'number',
                            label: 'Top-p',
                            tooltip: 'Nucleus-sampling cutoff: sample from the smallest set of tokens whose probabilities sum to this value.',
                            min: 0,
                            max: 1,
                        },
                        top_k: {
                            widget: 'number',
                            label: 'Top-k',
                            tooltip: 'Sample from the k most likely tokens at each step.',
                            min: 1,
                        },
                        batch_size: {
                            widget: 'number',
                            label: 'Batch size',
                            tooltip: 'Number of molecules generated per batch. Higher values can improve throughput at the cost of GPU memory.',
                            min: 1,
                        },
                        ...propertyFields(false),
                    },
                },
            ],
        },
    };

    /**
     * Turns the flat payload into the Molexar job-request payload.
     *
     * Only two things need doing by hand. `refLigandSpec` arrives already
     * resolved (by the `pocket` widget's resolver) into `{ proteinPdb,
     * ligandSdf }` and is renamed to the keys `molexar_activity.py` reads.
     * And `startStructure` — one descriptor holding both a typed SMILES
     * and a drawn molfile — is split into the two request keys the backend
     * expects, with the drawn structure winning: `molexar_run.py`'s
     * `resolve_start_smiles()` converts a molfile with full RDKit, which
     * preserves the `[*]` attachment points and multi-fragment `.`
     * drawings that the non-de_novo tasks depend on.
     *
     * Everything else rides through untouched: numbers arrive already cast
     * (`NumberWidget` casts at the source), blank fields never reach this
     * method at all, and each surviving key is already named exactly as
     * `PASSTHROUGH_PARAMS` expects.
     * @param {object} payload
     * @returns {Promise<{ requestData?: object, error?: string[] }>}
     */
    static async buildRequestPayload(payload) {
        const {
            refLigandSpec: { proteinPdb, ligandSdf },
            startStructure,
            ...rest
        } = payload;
        const { smiles, molfile } = startStructure || {};
        const startKeys = {};
        if (molfile) {
            startKeys.start_molfile = molfile;
        } else if ((smiles || '').trim()) {
            startKeys.start_smiles = smiles.trim();
        }
        return {
            requestData: {
                protein_pdb: proteinPdb,
                ref_ligand_sdf: ligandSdf,
                ...startKeys,
                ...rest,
            },
        };
    }

    /**
     * Results-table columns for generated compounds.
     * @override
     * @returns {Object<string, import('../JobDefinition').DataInfoEntry>}
     */
    static getDataInfo() {
        return {
            SMILES: {
                label: 'Structure', structureValue: true, defaultVisible: true, propApplicability: PropApplicability.PERCOMPOUND,
            },
            name: {
                label: 'Name', type: 'string', defaultVisible: true, propApplicability: PropApplicability.PERCOMPOUND,
            },
        };
    }

    /**
     * Parses the Molexar `.smi` output into generated structures and stores them
     * on local job state for the results table / import.
     * @override
     * @param {object} job
     * @param {object|string} results
     * @param {import('../JobDefinition').IntegrateResultsOptions} [_options]
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(job, results, _options) {
        let resultsPacket = results;
        let structures = [];
        let error;
        try {
            if (!resultsPacket) return { error: 'Empty results' };
            if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
            const smi = resultsPacket.jobResults?.smi;
            if (!smi) return { error: 'Empty results' };
            structures = parseMolexarSmi(smi).map((smiles, i) => ({
                id: `${job.jobid}_${i}`,
                name: `molexar_${i + 1}`,
                SMILES: smiles || null,
            }));
            updateLocalJobState(job.jobid, { structures });
        } catch (ex) {
            error = `Error loading ${this.jobTypeId} results`;
            console.error(error, ex);
        }
        return {
            error,
            results: { structures, jobResults: resultsPacket.jobResults },
        };
    }
}

/**
 * Parse Molexar's `.smi` output: one canonical SMILES per line, no header. A
 * `.smi` line may optionally carry a whitespace-separated name after the SMILES,
 * so keep only the first token per non-empty line.
 * @param {string} smi
 * @returns {string[]} canonical SMILES strings
 */
function parseMolexarSmi(smi) {
    return smi
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l !== '')
        .map((l) => l.split(/\s+/)[0]);
}
